import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Flow } from '@thive/fddm-types';

import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { NumberInput } from '@/modules/common/components/inputs';
import { ClearButton } from '@/modules/common/components/inputs/types';
import { getCommonNumber } from '@/modules/common/helpers/commonValue';
import { DisabledReasonTooltip } from '@/modules/salesWorkspace/components/propertyPanel/layout/components/common/DisabledReasonTooltip';

const LABEL_RATIO = 70;

/** @description The common value of a field over the flows that have it, null (Mixed) when they differ */
const getCommonFlowValue = (flows: Flow[], getValue: (flow: Flow) => number | undefined) =>
  getCommonNumber(flows.flatMap((flow) => getValue(flow) ?? []));

type Props = {
  flows: Flow[];
  labelKey: string;
  /** Why the field is disabled when only part of the selected flows have it */
  reasonKey: string;
  /** Whether a flow has this field */
  hasField: (flow: Flow) => boolean;
  getValue: (flow: Flow) => number | undefined;
  onChange: (value: number) => void;
  minValue?: number;
  unit?: string;
  skipFocus?: boolean;
  clearButton?: ClearButton;
};

/**
 * @description A field of the flows multi-edit. Shown when any selected flow has it, only editable
 * when all of them do, like the partial selections of the area panels.
 */
function FlowsMultiEditFieldComponent({
  flows,
  labelKey,
  reasonKey,
  hasField,
  getValue,
  onChange,
  minValue,
  unit,
  skipFocus,
  clearButton,
}: Props) {
  const { t } = useTranslation();

  const { flowsWithField, value } = useMemo(() => {
    const withField = flows.filter(hasField);
    return { flowsWithField: withField, value: getCommonFlowValue(withField, getValue) };
  }, [flows, getValue, hasField]);

  if (flowsWithField.length === 0) return null;

  const isPartialSelection = flowsWithField.length !== flows.length;

  return (
    <PropertiesLabel labelRatio={LABEL_RATIO} skipFocus={skipFocus} i18nkey={labelKey}>
      <DisabledReasonTooltip reasonKey={isPartialSelection ? reasonKey : undefined}>
        <NumberInput
          // undefined (not false) keeps NumberInput's own read-only-mode check
          disabled={isPartialSelection ? true : undefined}
          value={value}
          onChange={onChange}
          unit={unit}
          minValue={minValue}
          minValueErrorMsg={
            minValue === undefined ? undefined : t('common:minimum_value', { minValue })
          }
          clearButton={clearButton}
        />
      </DisabledReasonTooltip>
    </PropertiesLabel>
  );
}

export const FlowsMultiEditField = memo(FlowsMultiEditFieldComponent);
